import { useState, useEffect, useRef, useContext, useCallback, createContext } from "react";
import { post, C, THUMB_BASE, LOGO_BASE } from "./utils";
import { ThemeCtx, Icon, SORT_OPTIONS } from "./AppShell";
import { GalleryTile } from "./Dashboard";

// ─── Global filter context — any page can read the active filters ─────────────
export const FilterCtx = createContext({ filters: null, clearFilter: ()=>{} });

// ─── Helpers ──────────────────────────────────────────────────────────────────
const PAGE_SIZE  = 20;
const isImage    = (p="") => /\.(jpg|jpeg|png|gif|webp|bmp)$/i.test(p);
const fileName   = (p="") => p.split("/").pop();
const stripHtml  = (h="") => h.replace(/<[^>]*>/g," ").replace(/&nbsp;/g," ").replace(/\s+/g," ").trim();
const getPrimaryProduct    = (arr=[]) => arr.find(p=>(p.PRIMARY==="1"||p.PRIMARY===1)&&String(p.BRANDID)!=="0")?.BRANDNAME||"";
const getPrimaryIndication = (arr=[]) => arr.find(i=>(i.PRIMARY==="1"||i.PRIMARY===1)&&String(i.INDICATIONID)!=="0")?.INDICATIONNAME||"";
const getCompanyName = (c) => (c&&String(c.SPONSORID)!=="0")?c.SPONSORNAME||"":"";
const getCompanyLogo = (c) => (c&&String(c.SPONSORID)!=="0"&&c.COMPANY_LOGO)?LOGO_BASE+c.COMPANY_LOGO:null;
const MONTHS = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
const toApiDate = (dateStr) => {
  if(!dateStr) return null;
  const d = new Date(dateStr+"T00:00:00");
  if(isNaN(d)) return null;
  const dd = String(d.getDate()).padStart(2,"0");
  const mon = MONTHS[d.getMonth()];
  const yy = String(d.getFullYear()).slice(-2);
  return `${dd}-${mon}-${yy}`;
};
const todayStr = () => new Date().toISOString().split("T")[0];

export const EMPTY_FILTER = {
  fromDate:"", toDate:"",
  categories:[], sponsors:[], brands:[], indications:[],
  keyword:"", acronym:null, status:null,
};

export function hasActiveFilters(f) {
  if(!f) return false;
  return !!(f.fromDate||f.toDate||f.categories?.length||f.sponsors?.length||
    f.brands?.length||f.indications?.length||f.keyword||f.acronym||f.status);
}

export function buildFilterParams(f) {
  if(!f) return {};
  const p = {};
  if(f.fromDate)           p.startdate     = toApiDate(f.fromDate);
  if(f.toDate)             p.enddate       = toApiDate(f.toDate);
  if(f.categories?.length) p.doctypeid     = f.categories.map(c=>c.DOCTYPEID).join(",");
  if(f.sponsors?.length)   p.sponsorid     = f.sponsors.map(s=>s.SPONSORID).join(",");
  if(f.brands?.length)     p.brandid       = f.brands.map(b=>b.BRANDID).join(",");
  if(f.indications?.length)p.indicationid  = f.indications.map(i=>i.INDICATIONID).join(",");
  if(f.keyword)            p.search        = f.keyword;
  if(f.acronym?.ACRONYMSID) p.acronymsid   = f.acronym.ACRONYMSID;
  if(f.status)             p.stateid       = Number(f.status.id);
  return p;
}

// ─── Pill toggle ──────────────────────────────────────────────────────────────
function PillGroup({ items=[], value=[], onChange, idKey, labelKey, color, multi=true }) {
  const [search, setSearch] = useState("");
  const isMobile = useContext(ThemeCtx)?.isMobile || false;
  const filtered = items.filter(i=>(i[labelKey]||"").toLowerCase().includes(search.toLowerCase()));
  const isSelected = item => multi
    ? value.some(v=>String(v[idKey])===String(item[idKey]))
    : String(value?.[idKey])===String(item[idKey]);
  const toggle = item => {
    if(multi) isSelected(item)
      ? onChange(value.filter(v=>String(v[idKey])!==String(item[idKey])))
      : onChange([...value, item]);
    else onChange(isSelected(item) ? null : item);
  };
  return(
    <div>
      {!isMobile&&items.length>8&&(
        <input value={search} onChange={e=>setSearch(e.target.value)}
          placeholder="Search…"
          style={{width:"100%",padding:"5px 9px",border:"1px solid #E2E8F0",borderRadius:"6px",
            fontSize:"11px",marginBottom:"8px",outline:"none",fontFamily:"inherit",
            boxSizing:"border-box"}}/>
      )}
      <div style={{display:"flex",flexWrap:"wrap",gap:"5px"}}>
        {filtered.map(item=>{
          const sel = isSelected(item);
          return(
            <button key={item[idKey]} onClick={()=>toggle(item)}
              style={{padding:isMobile?"6px 14px":"4px 11px",borderRadius:"20px",cursor:"pointer",outline:"none",
                border:`1.5px solid ${sel?color:"#E2E8F0"}`,
                background:sel?color:"#fff",color:sel?"#fff":"#374151",
                fontSize:isMobile?"13px":"12px",fontWeight:sel?"600":"400",transition:"all .12s"}}>
              {item[labelKey]}
            </button>
          );
        })}
        {!filtered.length&&<span style={{fontSize:"11px",color:C.muted}}>No results</span>}
      </div>
    </div>
  );
}

// ─── Filter panel (slide-in drawer over current page) ────────────────────────
// ── Export button ─────────────────────────────────────────────────────────────
function ExportButton({ filters, user, subproject, color }) {
  const [state, setState] = useState("idle"); // idle | fetching | exporting | done | error
  const [msg,   setMsg  ] = useState("");

  const handleExport = async () => {
    if(!hasActiveFilters(filters)) {
      setMsg("Apply at least one filter before exporting."); return;
    }
    setState("fetching"); setMsg("");
    try {
      // Step 1 — fetch ALL doc IDs (no pagination)
      const extra = buildFilterParams(filters);
      const params = {
        subprojectid: subproject.SUBPROJECTID,
        userid:       user.USERID,
        stateid:      extra.stateid || 2,
        first:        0,
        last:         9999,
        sort:         "latest",
        ...extra,
      };
      const r = await post("advance_filter", params, user.AUTH_CODE);
      if(r.STATUS!=="1"||!Array.isArray(r.DATA)||!r.DATA.length) {
        setState("error"); setMsg("No documents found to export."); return;
      }
      const ids = r.DATA.map(d=>d.DOCUMENTID).filter(Boolean);
      if(!ids.length) { setState("error"); setMsg("No documents found."); return; }

      // Step 2 — call export API with comma-separated IDs
      setState("exporting"); setMsg(`Exporting ${ids.length} document${ids.length!==1?"s":""}…`);
      const fd = new FormData();
      fd.append("userid",     user.USERID);
      fd.append("documentid", ids.join(","));
      const res = await fetch("https://cip.medsphere.net/api/export",
        { method:"POST", headers:{"Auth-Code":user.AUTH_CODE}, body:fd });
      const json = await res.json().catch(()=>null);
      console.log("Export API response:", json);
      if(!json) {
        setState("error"); setMsg("Empty response from server."); return;
      }
      // Show full response message regardless of status
      const apiMsg = json.MESSAGE||json.MSG||json.message||json.msg||JSON.stringify(json);
      if(json.STATUS==="1"||json.STATUS===1||json.status===1||json.status==="1") {
        if(json.URL||json.FILE||json.PATH||json.url||json.file||json.path) {
          const url = json.URL||json.FILE||json.PATH||json.url||json.file||json.path;
          window.open(url, "_blank");
        }
        setState("done");
        setMsg(`✅ ${ids.length} doc${ids.length!==1?"s":""} exported! ${apiMsg&&apiMsg!=="{}"?"· "+apiMsg:""}`);
        setTimeout(()=>{ setState("idle"); setMsg(""); }, 5000);
      } else {
        setState("error");
        setMsg(`❌ ${apiMsg||"Export failed"}`);
      }
    } catch(e) {
      setState("error"); setMsg("Network error. Please try again.");
    }
  };

  const busy = state==="fetching"||state==="exporting";

  return(
    <div>
      {msg&&(
        <div style={{marginBottom:"8px",fontSize:"12px",padding:"10px 12px",borderRadius:"8px",
          lineHeight:1.5,wordBreak:"break-word",
          background:state==="error"?"#FEF2F2":state==="done"?"#F0FDF4":"#EFF6FF",
          color:state==="error"?"#DC2626":state==="done"?"#16A34A":"#1D4ED8",
          border:`1px solid ${state==="error"?"#FECACA":state==="done"?"#BBF7D0":"#BFDBFE"}`}}>
          {msg}
        </div>
      )}
      <button onClick={handleExport} disabled={busy||!hasActiveFilters(filters)}
        style={{display:"flex",alignItems:"center",gap:"8px",padding:"10px 16px",
          borderRadius:"8px",border:"none",width:"100%",fontFamily:"inherit",
          fontSize:"13px",fontWeight:"600",cursor:busy||!hasActiveFilters(filters)?"not-allowed":"pointer",
          background:busy?"#F1F5F9":hasActiveFilters(filters)?color:"#E2E8F0",
          color:busy?"#64748B":hasActiveFilters(filters)?"#fff":"#94A3B8",
          transition:"all .15s"}}>
        {busy
          ? <><div style={{width:"14px",height:"14px",border:"2px solid #94A3B8",
              borderTopColor:"#64748B",borderRadius:"50%",animation:"spin .8s linear infinite"}}/>
              {state==="fetching"?"Fetching IDs…":"Exporting…"}</>
          : <><svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
              <polyline points="7 10 12 15 17 10"/>
              <line x1="12" y1="15" x2="12" y2="3"/>
            </svg>
            Export to PPTX</>
        }
      </button>
    </div>
  );
}

export function FilterPanel({ user, subproject, color, isAdmin, onClose, onApply, initialFilters }) {
  const [bis,     setBis]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(initialFilters||EMPTY_FILTER);
  const ctx      = useContext(ThemeCtx)||{};
  const isMobile = ctx?.isMobile || false;
  const hasNav   = !!document.querySelector("[data-leftnav]");

  const trials  = (bis?.["CLINICAL TRIALS"]||[]).filter(t=>t.ACRONYMS);
  const cats     = bis?.CATEGORY   || [];
  const brands   = bis?.BRAND      || [];
  const sponsors = bis?.COMPANY    || bis?.SPONSOR || [];
  const inds     = bis?.INDICATION || [];

  useEffect(()=>{
    if(!user?.USERID||!subproject?.SUBPROJECTID) return;
    setLoading(true);
    fetch(`https://cip.medsphere.net/api/bis?userid=${user.USERID}&subprojectid=${subproject.SUBPROJECTID}`,
      {headers:{"Auth-Code":user.AUTH_CODE}})
      .then(r=>r.json())
      .then(res=>{ setBis(res.DATA||res); setLoading(false); })
      .catch(()=>setLoading(false));
  },[subproject?.SUBPROJECTID]);

  const set = (key,val) => setFilters(f=>({...f,[key]:val}));

  const STATUS = [{id:"2",label:"Active"},{id:"1",label:"Inactive"},{id:"4",label:"Archive"}];

  const activeCount = [
    filters.fromDate||filters.toDate,
    filters.categories?.length, filters.sponsors?.length,
    filters.brands?.length, filters.indications?.length,
    filters.keyword, filters.acronym, filters.status,
  ].filter(Boolean).length;

  return(
    <>
      {/* Backdrop */}
      <div onClick={onClose}
        style={{position:"fixed",top:"48px",left:isMobile?0:hasNav?"200px":"0px",right:0,bottom:0,
          zIndex:498,background:"rgba(0,0,0,.3)",backdropFilter:"blur(1px)"}}/>

      {/* Drawer */}
      <div style={{position:"fixed",top:"48px",right:0,bottom:isMobile?"56px":0,
        width:isMobile?"100%":"440px",
        zIndex:499,background:"#fff",display:"flex",flexDirection:"column",
        boxShadow:"-8px 0 40px rgba(0,0,0,.12)"}}>

        {/* Header */}
        <div style={{flexShrink:0,display:"flex",alignItems:"center",
          padding:"16px 20px",borderBottom:"1px solid #F1F5F9"}}>
          <div>
            <div style={{fontSize:"15px",fontWeight:"700",color:"#0F172A"}}>
              Advanced Filter
            </div>
            {activeCount>0&&(
              <div style={{fontSize:"11px",color,fontWeight:"600",marginTop:"1px"}}>
                {activeCount} filter{activeCount!==1?"s":""} selected
              </div>
            )}
          </div>
          <div style={{flex:1}}/>
          <button onClick={()=>setFilters(EMPTY_FILTER)}
            style={{padding:"5px 12px",borderRadius:"7px",border:"1px solid #E2E8F0",
              background:"#F8FAFC",cursor:"pointer",fontFamily:"inherit",
              fontSize:"12px",color:"#64748B",marginRight:"8px"}}>
            Clear all
          </button>
          <button onClick={onClose}
            style={{width:"30px",height:"30px",borderRadius:"50%",border:"none",
              background:"#F1F5F9",color:"#64748B",cursor:"pointer",
              display:"flex",alignItems:"center",justifyContent:"center",fontSize:"18px"}}>
            ×
          </button>
        </div>

        {/* Body */}
        <div style={{flex:1,overflowY:"auto",padding:"0 20px"}}>
          {loading?(
            <div style={{display:"flex",alignItems:"center",justifyContent:"center",
              padding:"40px",color:C.muted,gap:"8px"}}>
              <div style={{width:"14px",height:"14px",border:`2px solid ${C.border}`,
                borderTopColor:color,borderRadius:"50%",animation:"spin .8s linear infinite"}}/>
              Loading filters…
              <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
            </div>
          ):(
            <div style={{display:"flex",flexDirection:"column",gap:"0"}}>

              {/* Date range */}
              <Section title="Date Range">
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:"8px"}}>
                  {[{key:"fromDate",label:"From"},{key:"toDate",label:"To"}].map(({key,label})=>(
                    <div key={key}>
                      <div style={{fontSize:"11px",color:"#94A3B8",marginBottom:"4px"}}>{label}</div>
                      <input type="date" value={filters[key]||""}
                        onChange={e=>set(key,e.target.value)}
                        style={{width:"100%",padding:"7px 9px",borderRadius:"7px",
                          border:"1px solid #E2E8F0",fontSize:"12px",outline:"none",
                          fontFamily:"inherit",boxSizing:"border-box",color:"#0F172A"}}/>
                    </div>
                  ))}
                </div>
              </Section>

              {cats.length>0&&(
                <Section title="Category">
                  <PillGroup items={cats} value={filters.categories}
                    onChange={v=>set("categories",v)}
                    idKey="DOCTYPEID" labelKey="DOCTYPENAME" color={color} multi={true}/>
                </Section>
              )}

              {sponsors.length>0&&(
                <Section title="Company">
                  <PillGroup items={sponsors} value={filters.sponsors}
                    onChange={v=>set("sponsors",v)}
                    idKey="SPONSORID" labelKey="SPONSORNAME" color={color} multi={true}/>
                </Section>
              )}

              {brands.length>0&&(
                <Section title="Product">
                  <PillGroup items={brands} value={filters.brands}
                    onChange={v=>set("brands",v)}
                    idKey="BRANDID" labelKey="BRANDNAME" color={color} multi={true}/>
                </Section>
              )}

              {inds.length>0&&(
                <Section title="Indication">
                  <PillGroup items={inds} value={filters.indications}
                    onChange={v=>set("indications",v)}
                    idKey="INDICATIONID" labelKey="INDICATIONNAME" color={color} multi={true}/>
                </Section>
              )}

              <Section title="Keyword / Tag">
                <input value={filters.keyword} onChange={e=>set("keyword",e.target.value)}
                  placeholder="#oncology, keynote…"
                  style={{width:"100%",padding:"8px 10px",borderRadius:"7px",
                    border:"1px solid #E2E8F0",fontSize:"13px",outline:"none",
                    fontFamily:"inherit",boxSizing:"border-box"}}/>
              </Section>

              {trials.length>0&&(
                <Section title="Acronym (Clinical Trial)">
                  <select value={filters.acronym?.ACRONYMSID||""}
                    onChange={e=>set("acronym", trials.find(t=>String(t.ACRONYMSID)===e.target.value)||null)}
                    style={{width:"100%",padding:"8px 10px",borderRadius:"7px",
                      border:"1px solid #E2E8F0",fontSize:"13px",outline:"none",
                      fontFamily:"inherit",boxSizing:"border-box",background:"#fff",cursor:"pointer"}}>
                    <option value="">All trials</option>
                    {trials.map(t=>(
                      <option key={t.ACRONYMSID} value={t.ACRONYMSID}>{t.ACRONYMS}</option>
                    ))}
                  </select>
                </Section>
              )}

              {isAdmin&&(
                <Section title="Status">
                  <PillGroup items={STATUS} value={filters.status}
                    onChange={v=>set("status",v)}
                    idKey="id" labelKey="label" color={color} multi={false}/>
                </Section>
              )}

              {/* Export — only shown if subproject.EXPORT===1 */}
              {isAdmin&&subproject?.EXPORT===1&&(
                <Section title="Export">
                  <ExportButton filters={filters} user={user} subproject={subproject} color={color}/>
                </Section>
              )}

            </div>
          )}
        </div>

        {/* Footer */}
        <div style={{flexShrink:0,padding:"16px 20px",borderTop:"1px solid #F1F5F9",
          display:"flex",gap:"8px"}}>
          <button onClick={onClose}
            style={{flex:1,padding:"11px",borderRadius:"9px",
              border:"1px solid #E2E8F0",background:"#F8FAFC",
              cursor:"pointer",fontFamily:"inherit",fontSize:"14px",
              fontWeight:"600",color:"#64748B"}}>
            Cancel
          </button>
          <button onClick={()=>onApply(filters)}
            disabled={!hasActiveFilters(filters)}
            style={{flex:2,padding:"11px",borderRadius:"9px",border:"none",
              background:hasActiveFilters(filters)?color:"#E2E8F0",
              color:hasActiveFilters(filters)?"#fff":"#94A3B8",
              cursor:hasActiveFilters(filters)?"pointer":"not-allowed",
              fontFamily:"inherit",fontSize:"14px",fontWeight:"700",
              transition:"all .15s"}}>
            Apply Filter
          </button>
        </div>
      </div>
    </>
  );
}

// ─── Section wrapper ──────────────────────────────────────────────────────────
function Section({ title, children }) {
  return(
    <div style={{padding:"16px 0",borderBottom:"1px solid #F8FAFC"}}>
      <div style={{fontSize:"10px",fontWeight:"700",color:"#94A3B8",
        textTransform:"uppercase",letterSpacing:".7px",marginBottom:"10px"}}>
        {title}
      </div>
      {children}
    </div>
  );
}

// ─── Active filter chips bar (shown below AppShell header when filter active) ─
export function FilterChipsBar({ filters, color, onRemove, onClearAll }) {
  if(!hasActiveFilters(filters)) return null;
  const chips = [];
  if(filters.fromDate||filters.toDate)
    chips.push({key:"date",label:`${filters.fromDate||"…"} → ${filters.toDate||"…"}`});
  filters.categories?.forEach(c=>chips.push({key:`cat_${c.DOCTYPEID}`,label:c.DOCTYPENAME}));
  filters.sponsors?.forEach(s=>chips.push({key:`sp_${s.SPONSORID}`,label:s.SPONSORNAME}));
  filters.brands?.forEach(b=>chips.push({key:`br_${b.BRANDID}`,label:b.BRANDNAME}));
  filters.indications?.forEach(i=>chips.push({key:`ind_${i.INDICATIONID}`,label:i.INDICATIONNAME}));
  if(filters.keyword) chips.push({key:"kw",label:`"${filters.keyword}"`});
  if(filters.acronym) chips.push({key:"ac",label:filters.acronym.ACRONYMS});
  if(filters.status)  chips.push({key:"st",label:filters.status.label});

  return(
    <div style={{flexShrink:0,display:"flex",alignItems:"center",gap:"6px",
      padding:"6px 16px",background:`${color}08`,
      borderBottom:`1px solid ${color}20`,flexWrap:"wrap"}}>
      <svg width="12" height="12" viewBox="0 0 24 24" fill="none"
        stroke={color} strokeWidth="2" strokeLinecap="round">
        <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
      </svg>
      <span style={{fontSize:"11px",fontWeight:"700",color,marginRight:"2px"}}>
        Filtered:
      </span>
      {chips.map(chip=>(
        <span key={chip.key} style={{display:"inline-flex",alignItems:"center",gap:"3px",
          padding:"2px 8px",borderRadius:"20px",fontSize:"11px",fontWeight:"600",
          background:`${color}15`,color,border:`1px solid ${color}30`,cursor:"default"}}>
          {chip.label}
          <span onClick={()=>onRemove(chip.key)}
            style={{cursor:"pointer",opacity:.6,fontSize:"13px",lineHeight:1,marginLeft:"1px"}}
            title="Remove this filter">×</span>
        </span>
      ))}
      <button onClick={onClearAll}
        style={{marginLeft:"auto",padding:"2px 10px",borderRadius:"20px",
          border:`1px solid ${color}40`,background:"transparent",color,
          cursor:"pointer",fontSize:"11px",fontWeight:"700",fontFamily:"inherit"}}>
        Clear all ×
      </button>
    </div>
  );
}

const ACT_DEACT_API = "https://cip.medsphere.net/api/act_deact_multiple";

// ── BulkActionBar ─────────────────────────────────────────────────────────────
function BulkActionBar({ selected, total, onSelectAll, onClearAll, onAction, loading, color }) {
  const count = selected.length;
  return(
    <div style={{position:"sticky",top:0,zIndex:50,background:"#fff",
      borderBottom:`1px solid ${C.border}`,padding:"8px 20px",
      display:"flex",alignItems:"center",gap:10,
      boxShadow:"0 2px 8px rgba(0,0,0,0.06)"}}>
      <label style={{display:"flex",alignItems:"center",gap:6,cursor:"pointer",
        fontSize:13,fontWeight:600,color:C.text,userSelect:"none"}}>
        <input type="checkbox"
          checked={count>0&&count===total}
          ref={el=>{ if(el) el.indeterminate=count>0&&count<total; }}
          onChange={e=>e.target.checked?onSelectAll():onClearAll()}
          style={{width:15,height:15,cursor:"pointer",accentColor:color}}/>
        {count===0?"Select all":count===total?`All ${total} selected`:`${count} of ${total} selected`}
      </label>
      <div style={{flex:1}}/>
      {count>0&&(
        <>
          <button onClick={()=>onAction(2)} disabled={loading}
            style={{padding:"6px 14px",borderRadius:7,border:"none",
              background:"#22C55E",color:"#fff",fontSize:12,fontWeight:600,
              cursor:loading?"not-allowed":"pointer",opacity:loading?0.6:1}}>
            ✓ Activate ({count})
          </button>
          <button onClick={()=>onAction(1)} disabled={loading}
            style={{padding:"6px 14px",borderRadius:7,border:"none",
              background:"#F59E0B",color:"#fff",fontSize:12,fontWeight:600,
              cursor:loading?"not-allowed":"pointer",opacity:loading?0.6:1}}>
            ⊘ Deactivate ({count})
          </button>
          <button onClick={()=>onAction(4)} disabled={loading}
            style={{padding:"6px 14px",borderRadius:7,border:"none",
              background:"#EF4444",color:"#fff",fontSize:12,fontWeight:600,
              cursor:loading?"not-allowed":"pointer",opacity:loading?0.6:1}}>
            🗑 Delete ({count})
          </button>
          <button onClick={onClearAll} disabled={loading}
            style={{padding:"6px 10px",borderRadius:7,
              border:`1px solid ${C.border}`,background:"transparent",
              color:C.muted,fontSize:12,cursor:"pointer"}}>
            ✕ Clear
          </button>
        </>
      )}
    </div>
  );
}

// ── SelectableTile ────────────────────────────────────────────────────────────
function SelectableTile({ doc, selected, onToggle, onOpen, subId, showDocType }) {
  const [hovered, setHovered] = useState(false);
  const isSelected = selected.includes(doc.DOCUMENTID);
  return(
    <div style={{position:"relative"}}
      onMouseEnter={()=>setHovered(true)}
      onMouseLeave={()=>setHovered(false)}>
      {(hovered||isSelected)&&(
        <div onClick={e=>{e.stopPropagation();onToggle(doc.DOCUMENTID);}}
          style={{position:"absolute",top:8,left:8,zIndex:10,
            width:20,height:20,borderRadius:5,
            background:isSelected?C.primary:"rgba(255,255,255,0.9)",
            border:`2px solid ${isSelected?C.primary:C.border}`,
            display:"flex",alignItems:"center",justifyContent:"center",
            cursor:"pointer",boxShadow:"0 1px 4px rgba(0,0,0,0.15)"}}>
          {isSelected&&(
            <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
              <path d="M1.5 5L4 7.5L8.5 2.5" stroke="#fff" strokeWidth="1.8"
                strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          )}
        </div>
      )}
      {isSelected&&(
        <div style={{position:"absolute",inset:0,borderRadius:8,zIndex:5,
          border:`2px solid ${C.primary}`,
          background:"rgba(24,95,165,0.06)",pointerEvents:"none"}}/>
      )}
      <GalleryTile doc={doc} showDocType={showDocType}
        onOpen={d=>onOpen(d,subId)}/>
    </div>
  );
}

// ── HashtagBar ────────────────────────────────────────────────────────────────
function HashtagBar({ hashtags, active, onChange }) {
  if(!hashtags.length) return null;
  return(
    <div style={{display:"flex",gap:"6px",marginBottom:"12px",overflowX:"auto",
      paddingBottom:"2px",scrollbarWidth:"none"}}>
      {["All",...hashtags].map(tag=>{
        const sel = tag==="All"?!active:active===tag;
        return(
          <button key={tag} onClick={()=>onChange(tag==="All"?null:tag)}
            style={{flexShrink:0,padding:"4px 12px",borderRadius:"20px",
              border:`1px solid ${sel?C.primary:C.border}`,
              background:sel?C.primary:"#fff",color:sel?"#fff":C.muted,
              fontSize:"12px",fontWeight:sel?"600":"400",cursor:"pointer",
              transition:"all .12s",whiteSpace:"nowrap"}}>
            {tag}
          </button>
        );
      })}
    </div>
  );
}

export default function AdvancedFilterScreen({ user, subproject, appliedFilters, onModifyFilter }) {
  const ctx     = useContext(ThemeCtx)||{};
  const color   = ctx.theme?.primary||C.primary;
  const [docs,      setDocs]     = useState([]);
  const [hashtags,  setHashtags] = useState([]);
  const [activeTag, setActiveTag]= useState(null);
  const [loading,   setLoading]  = useState(false);
  const [hasMore,   setHasMore]  = useState(false);
  const [sort,      setSort]     = useState("latest");
  const [selected,  setSelected] = useState([]);
  const [actLoading,setActLoading]=useState(false);
  const [actMsg,    setActMsg]   = useState(null);
  const pageRef  = useRef(0);
  const didInit  = useRef(false);

  const toggleSelect = (id) =>
    setSelected(prev=>prev.includes(id)?prev.filter(x=>x!==id):[...prev,id]);
  const selectAll = () => setSelected(docs.map(d=>d.DOCUMENTID));
  const clearAll  = () => setSelected([]);

  const handleBulkAction = async (stateid) => {
    if(!selected.length) return;
    const label = stateid===2?"Activate":stateid===1?"Deactivate":"Delete";
    if(stateid===4&&!window.confirm(
      `Delete ${selected.length} document${selected.length!==1?"s":""}? This cannot be undone.`
    )) return;
    setActLoading(true); setActMsg(null);
    try {
      const fd = new FormData();
      fd.append("subprojectid", subproject.SUBPROJECTID);
      fd.append("userid",       user.USERID);
      fd.append("stateid",      stateid);
      fd.append("documentid",   selected.join(","));
      const res  = await fetch(ACT_DEACT_API,
        {method:"POST",headers:{"Auth-Code":user.AUTH_CODE},body:fd});
      const json = await res.json().catch(()=>null);
      if(json?.STATUS==="1"||json?.STATUS===1){
        setActMsg({type:"success",
          text:`${label}d ${selected.length} document${selected.length!==1?"s":""} successfully.`});
        setSelected([]);
        setTimeout(()=>{
          pageRef.current=0; setDocs([]); setHasMore(false);
          runSearch(appliedFilters,sort,true);
          setActMsg(null);
        },1800);
      } else {
        setActMsg({type:"error",text:json?.MESSAGE||json?.message||`${label} failed.`});
      }
    } catch {
      setActMsg({type:"error",text:"Network error. Please try again."});
    }
    setActLoading(false);
  };

  const openDocTab = (doc, subId) => {
    const base = window.location.origin + window.location.pathname;
    // Store stateid so FilterDetailTab fetches with correct stateid
    const sid = appliedFilters?.status?.id || "2";
    localStorage.setItem(`doc_stateid_${doc.DOCUMENTID}`, String(sid));
    window.open(`${base}?fdetail=1&doc=${doc.DOCUMENTID}&dt=${doc.DOCTYPEID}&sub=${subId||""}`, "_blank", "noopener,noreferrer");
  };

  const runSearch = useCallback(async (f, s=sort, reset=true) => {
    if(!subproject||!f||!hasActiveFilters(f)) return;
    setLoading(true);
    if(reset) setSelected([]);
    const first = reset?0:pageRef.current*PAGE_SIZE;
    const extra = buildFilterParams(f);
    const params = {
      subprojectid: subproject.SUBPROJECTID,
      userid:       user.USERID,
      stateid:      extra.stateid||2,
      first, last:  PAGE_SIZE,
      sort:         s,
      ...extra,
    };
    try {
      const r = await post("advance_filter", params, user.AUTH_CODE);
      if(r.STATUS==="1"&&Array.isArray(r.DATA)&&r.DATA.length>0){
        setDocs(prev=>reset?r.DATA:[...prev,...r.DATA]);
        setHasMore(r.DATA.length===PAGE_SIZE);
        pageRef.current=reset?1:pageRef.current+1;
        if(reset){
          const all=new Set();
          if(Array.isArray(r.HASHTAGS)) r.HASHTAGS.forEach(t=>all.add(t));
          r.DATA.forEach(d=>(Array.isArray(d.HASHTAGS)?d.HASHTAGS:[]).forEach(t=>all.add(t)));
          setHashtags([...all].sort());
        }
      } else {
        if(reset) setDocs([]);
        setHasMore(false);
      }
    } catch{}
    setLoading(false);
  },[subproject,user,sort]);

  // Auto-run on mount with applied filters
  useEffect(()=>{
    if(didInit.current) return;
    didInit.current=true;
    if(appliedFilters&&hasActiveFilters(appliedFilters)){
      pageRef.current=0; setDocs([]); setHasMore(false);
      runSearch(appliedFilters, sort, true);
    }
  // eslint-disable-next-line
  },[]);

  // Re-run when appliedFilters changes (chip removed etc.)
  useEffect(()=>{
    if(!didInit.current) return;
    pageRef.current=0; setDocs([]); setHasMore(false);
    runSearch(appliedFilters, sort, true);
  // eslint-disable-next-line
  },[appliedFilters]);

  return(
    <div style={{display:"flex",flexDirection:"column",height:"100%",overflow:"hidden"}}>

      {/* bulk action bar */}
      {docs.length>0&&(
        <BulkActionBar selected={selected} total={docs.length}
          onSelectAll={selectAll} onClearAll={clearAll}
          onAction={handleBulkAction} loading={actLoading} color={color}/>
      )}

      {/* action feedback */}
      {actMsg&&(
        <div style={{margin:"10px 20px 0",padding:"10px 14px",borderRadius:8,fontSize:13,
          background:actMsg.type==="success"?"#F0FDF4":"#FEF2F2",
          color:actMsg.type==="success"?"#16A34A":"#DC2626",
          border:`1px solid ${actMsg.type==="success"?"#BBF7D0":"#FECACA"}`}}>
          {actMsg.text}
        </div>
      )}

      <div style={{flex:1,overflowY:"auto",padding:"20px"}}>
      <div style={{display:"flex",alignItems:"center",gap:"10px",marginBottom:"16px"}}>
        <div>
          <div style={{fontSize:"19px",fontWeight:"500",color:C.text}}>Filter Results</div>
          <div style={{fontSize:"13px",color:C.muted,marginTop:"1px"}}>
            {subproject?.SUMMARY} · {docs.length} result{docs.length!==1?"s":""}
          </div>
        </div>
        <div style={{flex:1}}/>
        {docs.length>0&&(
          <div style={{position:"relative"}}>
            <select value={sort} onChange={e=>{
              setSort(e.target.value); pageRef.current=0; setDocs([]);
              runSearch(appliedFilters,e.target.value,true);
            }}
              style={{padding:"6px 28px 6px 10px",borderRadius:"8px",border:`1px solid ${C.border}`,
                background:"#fff",color:C.muted,fontSize:"13px",outline:"none",
                cursor:"pointer",appearance:"none"}}>
              {SORT_OPTIONS.map(o=><option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <div style={{position:"absolute",right:"8px",top:"50%",transform:"translateY(-50%)",pointerEvents:"none"}}>
              <Icon name="chevron" size={12} color={C.hint}/>
            </div>
          </div>
        )}
      </div>

      <HashtagBar hashtags={hashtags} active={activeTag} onChange={tag=>{
        setActiveTag(tag);
        pageRef.current=0; setDocs([]); setHasMore(false);
        runSearch({...appliedFilters, keyword: tag||appliedFilters?.keyword||""}, sort, true);
      }}/>

      {loading&&docs.length===0&&(
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",
          gap:"8px",padding:"60px",color:C.muted,fontSize:"14px"}}>
          <div style={{width:"16px",height:"16px",border:`2px solid ${C.border}`,
            borderTopColor:color,borderRadius:"50%",animation:"spin .8s linear infinite"}}/>
          Searching…
          <style>{"@keyframes spin{to{transform:rotate(360deg)}}"}</style>
        </div>
      )}

      {!loading&&docs.length===0&&hasActiveFilters(appliedFilters)&&(
        <div style={{textAlign:"center",padding:"60px 20px",color:C.muted}}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" stroke={C.border} strokeWidth="1.5">
            <circle cx="11" cy="11" r="8"/><path d="m21 21-4.35-4.35"/>
          </svg>
          <div style={{fontSize:"15px",fontWeight:"600",color:C.text,marginTop:"12px"}}>No results found</div>
          <div style={{fontSize:"13px",marginTop:"4px"}}>Try adjusting your filters</div>
        </div>
      )}

      <div style={{display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:"12px"}}>
        {docs.map(doc=>(
          <SelectableTile key={doc.DOCUMENTID}
            doc={doc} selected={selected}
            onToggle={toggleSelect}
            onOpen={openDocTab}
            subId={subproject?.SUBPROJECTID}
            showDocType={true}/>
        ))}
      </div>

      {hasMore&&!loading&&(
        <div style={{textAlign:"center",marginTop:"24px"}}>
          <button onClick={()=>runSearch(appliedFilters,sort,false)}
            style={{padding:"9px 28px",borderRadius:"8px",border:`1px solid ${C.border}`,
              background:"#fff",color,fontSize:"14px",fontWeight:"500",cursor:"pointer"}}>
            Load more
          </button>
        </div>
      )}
      {loading&&docs.length>0&&(
        <div style={{display:"flex",alignItems:"center",justifyContent:"center",
          gap:"8px",padding:"16px",color:C.muted,fontSize:"13px"}}>
          <div style={{width:"14px",height:"14px",border:`2px solid ${C.border}`,
            borderTopColor:color,borderRadius:"50%",animation:"spin .8s linear infinite"}}/>
          Loading more…
        </div>
      )}
      </div>{/* end scrollable */}
    </div>
  );
}